import React, { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { CustomFieldDefinition } from "@/models/Organisation";
import { FormCard } from "@/components/Core/FormCard";
import CustomFieldsSection from "./CustomFieldsSection";
import {
	CustomFieldFormValues,
	buildCustomFieldsPayload,
	buildCustomFieldsPayloadWithStored,
	customFieldDefaults,
	customFieldsSchema,
	filterCustomFieldsByPlacement,
	groupOwnedCustomFields,
	inheritedCustomFieldsFromCustomer,
	jobFormCustomFieldDefinitions,
} from "./customFields.utils";

/** Customer definitions — two inheritable ones, one per customer card. */
const CUSTOMER_DEFINITIONS: CustomFieldDefinition[] = [
	{
		key: "avvikande-bg",
		entity: "customer",
		type: "boolean",
		label: { sv: "Avvikande bankgiro" },
		description: { sv: "Fakturor till kunden betalas till ett annat bankgiro." },
		placement: "faktura",
		order: 1,
	},
	{
		key: "export-id",
		entity: "customer",
		type: "string",
		label: { sv: "Export-ID" },
		description: { sv: "Säljar-ID i Visma.NET" },
		required: true,
		placement: "profil",
		order: 0,
	},
	{
		key: "kundens-kostnadsstalle",
		entity: "customer",
		type: "string",
		label: { sv: "Kundens kostnadsställe" },
		placement: "faktura",
		inheritance: "override",
		jobPlacement: "bokning",
		order: 0,
	},
	{
		key: "fakturaintervall",
		entity: "customer",
		type: "select",
		label: { sv: "Fakturaintervall" },
		options: [
			{ key: "veckovis", label: { sv: "Veckovis" } },
			{ key: "manadsvis", label: { sv: "Månadsvis" } },
		],
		defaultValue: "manadsvis",
		placement: "faktura",
		inheritance: "show",
		order: 2,
	},
	{
		key: "avtalsstart",
		entity: "customer",
		type: "date",
		label: { sv: "Avtalsstart" },
		placement: "profil",
		order: 1,
	},
];

/** Customer definitions with "Ägs av koncern" — on two different cards (the koncern form ignores placement). */
const GROUP_OWNED_DEFINITIONS: CustomFieldDefinition[] = [
	{
		key: "export-id",
		entity: "customer",
		type: "string",
		label: { sv: "Export-ID" },
		required: true,
		placement: "profil",
		order: 0,
	},
	{
		key: "ramavtalsnummer",
		entity: "customer",
		type: "string",
		label: { sv: "Ramavtalsnummer" },
		description: { sv: "Koncernens ramavtal med oss" },
		required: true,
		placement: "profil",
		groupOwned: true,
		order: 1,
	},
	{
		key: "samlingsfaktura",
		entity: "customer",
		type: "boolean",
		label: { sv: "Samlingsfaktura" },
		placement: "faktura",
		groupOwned: true,
		order: 2,
	},
];

/** The koncern's values for its owned fields (what a member's GET merges in). */
const KONCERN_VALUES = { ramavtalsnummer: "RA-2026-7", samlingsfaktura: true };

/** Job definitions — one per job card. */
const JOB_DEFINITIONS: CustomFieldDefinition[] = [
	{
		key: "projektkod",
		entity: "job",
		type: "string",
		label: { sv: "Projektkod" },
		placement: "bokning",
	},
	{
		key: "skyddsutrustning",
		entity: "job",
		type: "boolean",
		label: { sv: "Skyddsutrustning krävs" },
		placement: "ovrigt",
	},
];

/** What the customer has stored. */
const CUSTOMER = {
	customFields: {
		"avvikande-bg": true,
		"export-id": "S-42",
		"kundens-kostnadsstalle": "KST-100",
		fakturaintervall: "veckovis",
	},
};

type Form = { customFields: CustomFieldFormValues };

const Payload: React.FC<{ value: string }> = ({ value }) =>
	value ? (
		<pre className="rounded-md border border-gray-200 bg-gray-50 p-3 text-xs">{value}</pre>
	) : null;

const SubmitButton: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<button
		type="submit"
		className="rounded-md bg-brand px-3.5 py-1.5 text-sm font-medium text-brand-foreground"
	>
		{children}
	</button>
);

/**
 * Live example for the custom-fields section: a customer create form with the
 * two placement slices inline in their cards, a customer Profil tab that only
 * renders one slice but still sends the full map, and a job form where the
 * inheritable customer fields render in show / override mode.
 */
const CustomFieldsSectionExample: React.FC = () => {
	// --- Customer create: both slices, defaults, required validation -------
	const [customerPayload, setCustomerPayload] = useState("");
	const customerSchema = useMemo(
		() => yup.object().shape({ customFields: customFieldsSchema(CUSTOMER_DEFINITIONS) }),
		[]
	);
	const customerForm = useForm<Form>({
		mode: "onBlur",
		resolver: yupResolver(customerSchema),
		defaultValues: { customFields: customFieldDefaults(CUSTOMER_DEFINITIONS) },
	});
	const onCustomerSubmit = customerForm.handleSubmit((values) => {
		setCustomerPayload(
			JSON.stringify(buildCustomFieldsPayload(values.customFields, CUSTOMER_DEFINITIONS), null, 2)
		);
	});

	// --- Customer Profil tab: renders the profil slice only ----------------
	const profilDefinitions = filterCustomFieldsByPlacement(CUSTOMER_DEFINITIONS, "profil");
	const [profilPayload, setProfilPayload] = useState("");
	const profilForm = useForm<Form>({
		defaultValues: { customFields: { "export-id": "S-42", avtalsstart: "2026-01-01" } },
	});
	const onProfilSubmit = profilForm.handleSubmit((values) => {
		setProfilPayload(
			JSON.stringify(
				buildCustomFieldsPayloadWithStored(
					values.customFields,
					profilDefinitions,
					CUSTOMER_DEFINITIONS,
					CUSTOMER.customFields
				),
				null,
				2
			)
		);
	});

	// --- Job create: own fields + inherited customer fields ----------------
	const jobDefinitions = useMemo(
		() => jobFormCustomFieldDefinitions(JOB_DEFINITIONS, CUSTOMER_DEFINITIONS),
		[]
	);
	const inherited = useMemo(
		() => inheritedCustomFieldsFromCustomer(CUSTOMER, CUSTOMER_DEFINITIONS),
		[]
	);
	const [jobPayload, setJobPayload] = useState("");
	const jobSchema = useMemo(
		() =>
			yup
				.object()
				.shape({ customFields: customFieldsSchema(jobDefinitions, inherited, { onJob: true }) }),
		[jobDefinitions, inherited]
	);
	const jobForm = useForm<Form>({
		mode: "onBlur",
		resolver: yupResolver(jobSchema),
		defaultValues: { customFields: customFieldDefaults(jobDefinitions) },
	});
	const onJobSubmit = jobForm.handleSubmit((values) => {
		setJobPayload(
			JSON.stringify(
				buildCustomFieldsPayload(values.customFields, jobDefinitions, { onJob: true }),
				null,
				2
			)
		);
	});

	// --- Koncern-owned fields: member vs non-member, and the koncern form ---
	const [membership, setMembership] = useState<"member" | "member-unknown" | "none">("member");
	const corporateGroupMember = membership !== "none";
	const [groupOwnedPayload, setGroupOwnedPayload] = useState("");
	const groupOwnedSchema = useMemo(
		() =>
			yup.object().shape({
				customFields: customFieldsSchema(GROUP_OWNED_DEFINITIONS, undefined, {
					corporateGroupMember,
				}),
			}),
		[corporateGroupMember]
	);
	const groupOwnedForm = useForm<Form>({
		resolver: yupResolver(groupOwnedSchema),
		defaultValues: { customFields: customFieldDefaults(GROUP_OWNED_DEFINITIONS) },
	});
	const onGroupOwnedSubmit = groupOwnedForm.handleSubmit((values) => {
		setGroupOwnedPayload(
			JSON.stringify(
				buildCustomFieldsPayload(values.customFields, GROUP_OWNED_DEFINITIONS, {
					corporateGroupMember,
				}),
				null,
				2
			)
		);
	});
	const koncernDefinitions = useMemo(() => groupOwnedCustomFields(GROUP_OWNED_DEFINITIONS), []);
	const [koncernPayload, setKoncernPayload] = useState("");
	const koncernForm = useForm<Form>({
		defaultValues: { customFields: customFieldDefaults(koncernDefinitions) },
	});
	const onKoncernSubmit = koncernForm.handleSubmit((values) => {
		setKoncernPayload(
			JSON.stringify(buildCustomFieldsPayload(values.customFields, koncernDefinitions), null, 2)
		);
	});

	return (
		<div className="max-w-2xl space-y-8 p-6">
			<div>
				<h3 className="text-lg font-semibold">CustomFieldsSection</h3>
				<p className="text-sm text-muted-foreground">
					Fields render inside the host card their placement names. "Export-ID" is required,
					"Fakturaintervall" is a Lista with a default, "Avtalsstart" a date.
				</p>
			</div>

			<form onSubmit={onCustomerSubmit} className="space-y-4">
				<h4 className="text-base font-semibold">Customer create — inline in the two cards</h4>
				<FormCard title="BASIC_INFO">
					<p className="m-0 text-sm text-muted-foreground">(built-in inputs here)</p>
					<CustomFieldsSection
						entity="customer"
						placement="profil"
						variant="inline"
						definitions={CUSTOMER_DEFINITIONS}
						control={customerForm.control}
						register={customerForm.register}
						errors={customerForm.formState.errors}
					/>
				</FormCard>
				<FormCard title="INVOICE_RECIPIENT">
					<p className="m-0 text-sm text-muted-foreground">(built-in inputs here)</p>
					<CustomFieldsSection
						entity="customer"
						placement="faktura"
						variant="inline"
						definitions={CUSTOMER_DEFINITIONS}
						control={customerForm.control}
						register={customerForm.register}
						errors={customerForm.formState.errors}
					/>
				</FormCard>
				<SubmitButton>Bygg payload</SubmitButton>
				<Payload value={customerPayload} />
			</form>

			<form onSubmit={onProfilSubmit} className="space-y-4">
				<h4 className="text-base font-semibold">Customer Profil tab — subset + stored values</h4>
				<p className="text-sm text-muted-foreground">
					Only the profil slice is rendered; the faktura keys are read from the stored customer{" "}
					{JSON.stringify(CUSTOMER.customFields)} so the save does not clear them.
				</p>
				<CustomFieldsSection
					entity="customer"
					placement="profil"
					title="Profil-fält"
					definitions={CUSTOMER_DEFINITIONS}
					control={profilForm.control}
					register={profilForm.register}
				/>
				<SubmitButton>Bygg payload (full map)</SubmitButton>
				<Payload value={profilPayload} />
			</form>

			<form onSubmit={onJobSubmit} className="space-y-4">
				<h4 className="text-base font-semibold">Job create — inherited customer fields</h4>
				<p className="text-sm text-muted-foreground">
					"Kundens kostnadsställe" can be overridden (empty = inherit, the key is left out of the
					payload); "Fakturaintervall" is shown read-only. Both come from the customer.
				</p>
				<FormCard title="Bokning & referenser" skipTranslation>
					<p className="m-0 text-sm text-muted-foreground">(booking type, references…)</p>
					<CustomFieldsSection
						entity="job"
						placement="bokning"
						variant="inline"
						definitions={jobDefinitions}
						inherited={inherited}
						control={jobForm.control}
						register={jobForm.register}
						errors={jobForm.formState.errors}
					/>
				</FormCard>
				<CustomFieldsSection
					entity="job"
					placement="ovrigt"
					definitions={jobDefinitions}
					inherited={inherited}
					control={jobForm.control}
					register={jobForm.register}
					errors={jobForm.formState.errors}
				/>
				<SubmitButton>Bygg payload</SubmitButton>
				<Payload value={jobPayload} />
			</form>

			<form onSubmit={onGroupOwnedSubmit} className="space-y-4">
				<h4 className="text-base font-semibold">Customer form — "Ägs av koncern"</h4>
				<p className="text-sm text-muted-foreground">
					"Ramavtalsnummer" and "Samlingsfaktura" are owned by the koncern. A member sees the
					koncern's value read-only (not required, left out of the payload); a non-member edits them
					like any other field. "Koncern vald, ej sparad" is the create page right after a koncern
					is picked, before its values are known.
				</p>
				<div className="flex flex-wrap gap-2">
					{(
						[
							["member", "Medlem i koncern"],
							["member-unknown", "Koncern vald, ej sparad"],
							["none", "Ingen koncern"],
						] as const
					).map(([value, label]) => (
						<button
							key={value}
							type="button"
							onClick={() => setMembership(value)}
							className={
								membership === value
									? "rounded-md border border-solid border-blue-900 bg-blue-50 px-3 py-1 text-sm text-blue-900"
									: "rounded-md border border-solid border-gray-200 bg-white px-3 py-1 text-sm text-gray-700"
							}
						>
							{label}
						</button>
					))}
				</div>
				<FormCard title="BASIC_INFO">
					<CustomFieldsSection
						entity="customer"
						placement="profil"
						variant="inline"
						definitions={GROUP_OWNED_DEFINITIONS}
						control={groupOwnedForm.control}
						register={groupOwnedForm.register}
						errors={groupOwnedForm.formState.errors}
						corporateGroupMember={corporateGroupMember}
						corporateGroupValues={membership === "member" ? KONCERN_VALUES : undefined}
					/>
				</FormCard>
				<FormCard title="INVOICE_RECIPIENT">
					<CustomFieldsSection
						entity="customer"
						placement="faktura"
						variant="inline"
						definitions={GROUP_OWNED_DEFINITIONS}
						control={groupOwnedForm.control}
						register={groupOwnedForm.register}
						errors={groupOwnedForm.formState.errors}
						corporateGroupMember={corporateGroupMember}
						corporateGroupValues={membership === "member" ? KONCERN_VALUES : undefined}
					/>
				</FormCard>
				<SubmitButton>Bygg payload</SubmitButton>
				<Payload value={groupOwnedPayload} />
			</form>

			<form onSubmit={onKoncernSubmit} className="space-y-4">
				<h4 className="text-base font-semibold">Koncern form — owned fields, placement ignored</h4>
				<p className="text-sm text-muted-foreground">
					The koncern modal has no cards: every group-owned customer field renders in definition
					order (<code>ignorePlacement</code>), editable.
				</p>
				<CustomFieldsSection
					entity="customer"
					ignorePlacement
					variant="inline"
					definitions={koncernDefinitions}
					control={koncernForm.control}
					register={koncernForm.register}
				/>
				<SubmitButton>Bygg payload</SubmitButton>
				<Payload value={koncernPayload} />
			</form>

			<div>
				<h4 className="text-base font-semibold">No definitions on the card → renders nothing</h4>
				<CustomFieldsSection
					entity="consultant"
					placement="anstallning"
					definitions={[]}
					control={jobForm.control}
					register={jobForm.register}
				/>
			</div>
		</div>
	);
};

export default CustomFieldsSectionExample;
